import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

type Mode = 'password' | 'link-sent' | 'reset-sent'

export function LoginPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [mode, setMode] = useState<Mode>('password')

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
    if (error) {
      setError(error.message)
      setLoading(false)
    } else {
      navigate('/dashboard', { replace: true })
    }
  }

  const handleSendLink = async () => {
    if (!email.trim()) {
      setError('Enter your email address first')
      return
    }
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    })
    if (error) {
      setError(error.message)
      setLoading(false)
    } else {
      setMode('link-sent')
      setLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError('Enter your email address first')
      return
    }
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/auth/callback`,
    })
    if (error) {
      setError(error.message)
      setLoading(false)
    } else {
      setMode('reset-sent')
      setLoading(false)
    }
  }

  if (mode === 'link-sent' || mode === 'reset-sent') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-parchment">
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8 border border-parchment-dark text-center">
          <h1 className="text-3xl font-bold text-accent mb-4">HârnMaster</h1>
          <p className="text-ink mb-2">
            {mode === 'link-sent' ? 'Sign-in link sent.' : 'Password reset link sent.'}
          </p>
          <p className="text-ink-light text-sm mb-6">
            Check <strong>{email}</strong> and click the link in the email.
          </p>
          <button
            onClick={() => { setMode('password'); setError('') }}
            className="text-sm text-accent underline hover:text-accent-light"
          >
            Back to sign in
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-parchment">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8 border border-parchment-dark">
        <h1 className="text-3xl font-bold text-accent mb-2">HârnMaster</h1>
        <p className="text-ink-light mb-6">Sign in to your account.</p>

        <form onSubmit={handleSignIn}>
          <label htmlFor="email" className="block text-sm font-medium text-ink mb-1">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            className="w-full px-3 py-2 border border-parchment-dark rounded-md focus:outline-none focus:ring-2 focus:ring-accent mb-3"
          />

          <label htmlFor="password" className="block text-sm font-medium text-ink mb-1">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            className="w-full px-3 py-2 border border-parchment-dark rounded-md focus:outline-none focus:ring-2 focus:ring-accent mb-4"
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 bg-accent text-white rounded-md hover:bg-accent-light disabled:opacity-50 font-medium mb-3"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        {error && <p className="text-sm text-danger mb-3">{error}</p>}

        <div className="border-t border-parchment-dark pt-3 flex flex-col gap-2">
          <button
            type="button"
            onClick={handleSendLink}
            disabled={loading}
            className="text-sm text-ink-light hover:text-ink underline text-left disabled:opacity-50"
          >
            No password? Send me a sign-in link
          </button>
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={loading}
            className="text-sm text-ink-light hover:text-ink underline text-left disabled:opacity-50"
          >
            Forgot password? Send a reset link
          </button>
        </div>
      </div>
    </div>
  )
}
